// Shared ack-status → UI phase mapping for dock and drone command acks.
// Status vocabulary comes from the two device planes:
//   scripts/airscort/mavlink_bridge.py publish_ack()  → executing | done | rejected | error
//   mobile CommandState.wireStatus                    → held | dispatched | accepted
//                                                       | executing | done | rejected
//                                                       | error | timed_out

export type AckPhase = 'pending' | 'acked' | 'done' | 'failed';

/**
 * Detail stamped on a command whose pending window elapsed with no terminal ack.
 * Surfacing this as a failed state - instead of silently deleting the pending
 * entry - is what keeps a command from reading as fire-and-forget when the
 * hardware never answers. Used by both surfaces (useDockCommand / DockCommandBar
 * and useDroneCommand).
 */
export const NO_ACK_DETAIL = 'No acknowledgement received';

/**
 * In-flight statuses ordered weakest-claim first.
 *
 * `held` and `dispatched` are NOT the aircraft acknowledging anything: `held`
 * means the companion app is sitting on the command waiting for an operator ARM
 * and has sent it nowhere, and `dispatched` means an SDK method was invoked and
 * returned. Only `accepted` upwards is the aircraft's own answer. The command bar
 * uses this to render the first two as weaker than an acknowledgement, because
 * showing them as one reproduces the exact claim GAP-P0-04 removed.
 */
export const UNCONFIRMED_STATUSES: ReadonlySet<string> = new Set(['held', 'dispatched']);

/** True for a status the DEVICE reported that the AIRCRAFT has not confirmed. */
export function isUnconfirmedStatus(status: string | null | undefined): boolean {
  return status !== null && status !== undefined && UNCONFIRMED_STATUSES.has(status.toLowerCase());
}

/**
 * The non-terminal statuses that ARE the aircraft's own answer.
 *
 * An allowlist rather than the complement of [UNCONFIRMED_STATUSES], because the
 * two defaults are not equally safe. Anything unrecognised — a future device word
 * like `queued`, a new bridge's vocabulary, a missing status — must NOT be taken
 * as the aircraft having confirmed something, which is the claim this whole
 * change exists to stop the system making on no evidence. Callers deciding
 * whether silence is honest use this; unrecognised falls to the unconfirmed side.
 *
 * Terminal words (`done`, `rejected`, `timed_out`, …) are deliberately absent:
 * they are not in flight at all, and callers reach them via [ackPhaseFromStatus]
 * before this is consulted.
 */
const CONFIRMED_IN_FLIGHT_STATUSES: ReadonlySet<string> = new Set(['accepted', 'executing']);

/** True only for a non-terminal status the AIRCRAFT itself confirmed. */
export function isConfirmedInFlightStatus(status: string | null | undefined): boolean {
  return (
    status !== null &&
    status !== undefined &&
    CONFIRMED_IN_FLIGHT_STATUSES.has(status.toLowerCase())
  );
}

const DONE_STATUSES = new Set(['done', 'completed', 'success']);
// 'timed_out' is terminal and it is a FAILURE: the companion app gave up waiting
// for the aircraft's answer (GAP-P0-04). Leaving it to fall through to 'acked'
// would render "we never heard back" as a command still healthily in progress -
// the exact ambiguity the timeout exists to remove.
const FAILED_STATUSES = new Set(['rejected', 'error', 'failed', 'timed_out']);

/**
 * Map a device-reported ack status onto the UI lifecycle phase.
 * In-flight statuses (held, dispatched, accepted, executing) and unknown strings
 * render as 'acked' - the device answered, the command is not yet terminal. The
 * distinction between them is deliberately NOT in this coarse phase; read
 * isUnconfirmedStatus alongside it where the difference matters.
 */
export function ackPhaseFromStatus(status: string | null | undefined): AckPhase {
  if (!status) return 'acked';
  const normalized = status.toLowerCase();
  if (DONE_STATUSES.has(normalized)) return 'done';
  if (FAILED_STATUSES.has(normalized)) return 'failed';
  return 'acked';
}
